import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import {
  BlocoCarta,
  ServicoCarta,
  ServicoDetalhePublicoResponse,
} from '../models/servico-detalhe.model';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico`;

/** O erro fica local: a tela é pública e trata a falha ela mesma, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Ícone de acordeão para bloco personalizado, ou cujo título não está no catálogo da lei. */
const ICONE_BLOCO_PADRAO = 'file-text';

/**
 * Título da pergunta fixa → ícone Lucide.
 *
 * **A API não manda ícone de bloco** — não há coluna para isso, e criar uma seria inventar
 * requisito. O ícone é decisão de apresentação, e este mapa é onde ela vive.
 *
 * As chaves são os sete títulos de `BlocoServico.PerguntasFixas` no back-end, já
 * normalizados (sem acento, minúsculos, espaços colapsados). O casamento é por título
 * normalizado e **não** por igualdade exata: o servidor restaura o título dos blocos fixos
 * a partir do catálogo, mas um dado antigo pode divergir por caixa ou espaço — e um ícone
 * errado é muito melhor que uma tela quebrada.
 *
 * Bloco personalizado (o órgão pode criar até três) cai no ícone padrão, por definição:
 * o título é texto livre.
 */
const ICONES_POR_TITULO: Readonly<Record<string, string>> = {
  'o que e o servico?': 'circle-help',
  'requisitos, documentos e informacoes': 'file-text',
  'tabela de precos publicos e taxas': 'dollar-sign',
  'prazo de conclusao do servico': 'clock',
  'orgao responsavel e canais': 'building-2',
  'como solicitar este servico (passo a passo)': 'list',
  'principais etapas do servico': 'route',
};

/**
 * A carta de um serviço — o que alimenta os acordeões da página do serviço.
 *
 * ⚠️ **O portal não sabe nem envia o órgão.** O back-end o resolve pelo host de quem
 * chamou, como nas demais rotas públicas. Um serviço de outro órgão responde 404,
 * indistinguível de um id que não existe.
 *
 * ⚠️ **A busca é por `codServicoSrv`, não pelo slug da rota.** A URL do portal é o slug do
 * nome (`/agendamento-de-consulta`) e o back-end só conhece o id; quem faz a ponte é o
 * `CatalogoPublicoService`, cuja listagem já traz os dois. Por isso este service recebe o
 * código pronto, em `definirServico`, em vez de ler a rota.
 */
@Service()
export class ServicoDetalhePublicoService {
  /** Nulo enquanto o catálogo não resolveu o slug — e então nenhuma request é feita. */
  private readonly codServico = signal<number | null>(null);

  private readonly detalheApi = httpResource<ResponseApi<ServicoDetalhePublicoResponse>>(() => {
    const cod = this.codServico();

    // `undefined` é como o httpResource entende "ainda não há o que buscar". Sem esta
    // guarda, a tela dispararia uma chamada a `/servico/null` enquanto o catálogo carrega.
    if (cod === null) return undefined;

    return { url: `${RECURSO}/servico/${cod}`, context: ERRO_LOCAL() };
  });

  readonly carregando = computed(() => this.detalheApi.isLoading());

  readonly falhou = computed(() => this.detalheApi.error() !== undefined);

  /**
   * A carta, ou `null` enquanto não há serviço resolvido, a chamada está no ar ou falhou.
   *
   * O `error()` é consultado ANTES do `value()` de propósito: em estado de erro o `value()`
   * **lança**, e é a mesma tela que mostra o aviso de falha que lê este signal.
   */
  readonly carta = computed<ServicoCarta | null>(() => {
    if (this.detalheApi.error() !== undefined) return null;

    const corpo = this.detalheApi.value()?.data;

    return corpo ? paraCarta(corpo) : null;
  });

  /**
   * Aponta o service para um serviço. Passar `null` (rota estática, sem código) devolve o
   * service ao estado inicial, sem chamada nenhuma.
   */
  definirServico(codServicoSrv: number | null): void {
    this.codServico.set(codServicoSrv);
  }

  /** Refaz a chamada — é o "Tentar novamente" do aviso de falha. */
  recarregar(): void {
    this.detalheApi.reload();
  }
}

/** O contrato da API convertido no que a tela manipula. */
function paraCarta(corpo: ServicoDetalhePublicoResponse): ServicoCarta {
  return {
    id: corpo.codServicoSrv,
    titulo: corpo.desNomeSrv,
    resumo: corpo.desResumoSrv?.trim() ?? '',
    permiteAnonimo: corpo.bitPermitirAnonimoSrv,
    exigeGovBr: corpo.bitExigirGovBrSrv,
    publicadoEm: formatarData(corpo.datInicioSrv),
    atualizadoEm: formatarData(corpo.datAtualizacaoSrv),
    tiposAtendimento: corpo.tiposAtendimento ?? [],
    blocos: (corpo.blocos ?? []).map(paraBloco),
    orgaos: (corpo.orgaos ?? []).map(nomeDoOrgao).filter((nome) => nome.length > 0),
  };
}

/**
 * O bloco da API vira acordeão.
 *
 * O `id` sai da ordem porque **a API não expõe a PK do bloco** (`cod_bloco_servico_bsv` é
 * chave do adm, e o replace-all do wizard a troca a cada gravação). A ordem é estável
 * dentro de uma carta, que é tudo o que o controle de abrir/fechar precisa.
 */
function paraBloco(bloco: { desTituloBsv: string; desConteudoBsv: string | null; numOrdemBsv: number }): BlocoCarta {
  return {
    id: `bloco-${bloco.numOrdemBsv}`,
    titulo: bloco.desTituloBsv,
    conteudo: bloco.desConteudoBsv ?? '',
    icone: iconeDoBloco(bloco.desTituloBsv),
  };
}

/** "SEDUC — Secretaria de Educação", ou só o que houver. */
function nomeDoOrgao(orgao: { desNomeOrg: string | null; desSiglaOrg: string | null }): string {
  const sigla = orgao.desSiglaOrg?.trim() ?? '';
  const nome = orgao.desNomeOrg?.trim() ?? '';

  if (sigla && nome) return `${sigla} — ${nome}`;

  return nome || sigla;
}

/**
 * O ícone do acordeão, a partir do título normalizado.
 *
 * **Nunca devolve o título cru.** O `<i-lucide>` ignora nome fora do catálogo, mas o
 * contrato deste mapa é entregar sempre um nome válido — um acordeão sem ícone nenhum é
 * um defeito silencioso, e depender da guarda do componente é depender de outra camada.
 */
function iconeDoBloco(titulo: string): string {
  return ICONES_POR_TITULO[normalizarTitulo(titulo)] ?? ICONE_BLOCO_PADRAO;
}

/** Sem acento, minúsculo, espaços colapsados — a forma em que as chaves do mapa estão. */
function normalizarTitulo(titulo: string): string {
  return (titulo ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Data ISO da API em pt-BR; vazio para nulo, para a tela omitir a linha. */
function formatarData(iso: string | null): string {
  if (!iso) return '';

  const data = new Date(iso);

  return Number.isNaN(data.getTime()) ? '' : data.toLocaleDateString('pt-BR');
}
